import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, Pin, PinOff, Plus, Trash2, ExternalLink, Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FlyerComposer from "./FlyerComposer";

export function FlyerHeart({ item, onToggle, disabled }) {
  return (
    <button type="button" onClick={() => onToggle(item)} disabled={disabled} aria-pressed={item.hearted} aria-label={item.hearted ? "Remove heart" : "Heart this flyer"}
      data-testid={`flyer-heart-btn-${item.id}`}
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border transition-colors disabled:opacity-50 ${item.hearted ? "border-rose-500/60 bg-rose-600/20 text-rose-300" : "border-white/10 bg-black/40 text-zinc-400 hover:text-white hover:border-white/30"}`}>
      <Heart size={13} className={item.hearted ? "fill-current" : ""} />
      <span data-testid={`flyer-heart-count-${item.id}`}>{item.hearts || 0}</span>
    </button>
  );
}

function FlyerCard({ item, isAdmin, onPin, onDelete, onHeart, canHeart }) {
  const internal = !!item.link_url && (item.link_url.startsWith("/") || item.link_url.startsWith(window.location.origin));
  const Wrapper = internal ? Link : item.link_url ? "a" : "div";
  const wrapProps = internal ? { to: item.link_url.replace(window.location.origin, "") } : item.link_url ? { href: item.link_url, target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <article data-testid={`flyer-${item.id}`} className={`relative shrink-0 w-64 sm:w-72 snap-start border bg-zinc-950/70 overflow-hidden group ${item.pinned ? "border-amber-400/50" : "border-white/10"}`}>
      <Wrapper {...wrapProps} className="block" data-testid={`flyer-open-${item.id}`}>
        {item.image_url && <img src={item.image_url} alt={item.title} className="w-full h-40 object-cover" loading="lazy" />}
        <div className="p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm font-semibold text-white leading-snug" data-testid={`flyer-title-${item.id}`}>{item.title}</div>
            {item.link_url && !internal && <ExternalLink size={13} className="text-zinc-500 shrink-0 mt-0.5" />}
          </div>
          {item.body && <p className="text-xs text-zinc-400 mt-1 leading-relaxed line-clamp-3">{item.body}</p>}
          {item.expires_at && <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-2">Until {new Date(item.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>}
        </div>
      </Wrapper>
      <div className="px-3 pb-3"><FlyerHeart item={item} onToggle={onHeart} disabled={!canHeart} /></div>
      {item.pinned && <div className="absolute top-2 left-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-amber-400 text-black flex items-center gap-1" data-testid={`flyer-pinned-${item.id}`}><Pin size={10} /> Pinned</div>}
      {isAdmin && (
        <div className="absolute top-2 right-2 flex gap-1">
          <button type="button" onClick={() => onPin(item)} aria-label={item.pinned ? "Unpin" : "Pin"} data-testid={`flyer-pin-btn-${item.id}`} className="h-7 w-7 grid place-items-center bg-black/75 text-white hover:bg-amber-400 hover:text-black transition-colors">
            {item.pinned ? <PinOff size={13} /> : <Pin size={13} />}
          </button>
          <button type="button" onClick={() => onDelete(item)} aria-label="Delete" data-testid={`flyer-delete-btn-${item.id}`} className="h-7 w-7 grid place-items-center bg-black/75 text-white hover:bg-rose-600 transition-colors"><Trash2 size={13} /></button>
        </div>
      )}
    </article>
  );
}

// Admin flyer / announcement board at the top of the Feed (replaces the Battle Champions carousel).
export default function FlyerBoard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/announcements").then(({ data }) => setItems(data?.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const pin = async (item) => {
    try {
      const { data } = await api.patch(`/announcements/${item.id}`, { pinned: !item.pinned });
      setItems((list) => (list || []).map((x) => (x.id === item.id ? data : x)).sort((a, b) => (b.pinned - a.pinned) || (b.created_at > a.created_at ? 1 : -1)));
      toast.success(data.pinned ? "Pinned to top." : "Unpinned.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update."); }
  };
  const heart = async (item) => {
    try {
      const { data } = await api.post(`/announcements/${item.id}/heart`);
      setItems((list) => (list || []).map((x) => (x.id === item.id ? { ...x, hearted: data.hearted, hearts: data.hearts } : x)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't react."); }
  };
  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await api.delete(`/announcements/${item.id}`);
      setItems((list) => (list || []).filter((x) => x.id !== item.id));
      toast.success("Flyer removed.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't delete."); }
  };

  if (items === null) return null;
  if (items.length === 0 && !isAdmin) return null;

  return (
    <section className="card-surface p-4 mb-6 border-amber-400/20" data-testid="flyer-board">
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-2">
          <Megaphone size={16} className="text-amber-300" />
          <div className="font-display text-lg text-white">Announcements</div>
        </div>
        {isAdmin && (
          <button type="button" onClick={() => setComposerOpen(true)} data-testid="flyer-new-btn"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-1.5 bg-amber-400 text-black hover:bg-amber-300 transition-colors">
            <Plus size={13} /> Post flyer
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-zinc-500" data-testid="flyer-board-empty">Nothing posted yet — only admins see this empty board.</div>
      ) : (
        <div className="overflow-x-auto flex gap-3 pb-1 flex-nowrap snap-x snap-mandatory" data-testid="flyer-board-list">
          {items.map((it) => <FlyerCard key={it.id} item={it} isAdmin={isAdmin} onPin={pin} onDelete={remove} onHeart={heart} canHeart={!!user} />)}
        </div>
      )}
      {isAdmin && <FlyerComposer open={composerOpen} onOpenChange={setComposerOpen} onCreated={load} />}
    </section>
  );
}
